// Make changes — the second row of the mode's bar: ✓ Checks before saving,
// More actions ▾ and ✦ Ask AI to change, with the dialogs they open.
//
// Every dialog writes the transaction's core model through the SAME session
// as the rest of the mode: each Add / Remove / switch is one stored change and
// one Undo step. The More actions menu is data (./build/moreActions.json);
// the dialogs that work in place are After saving, Who sees which records,
// Lock records and Filters; the rest ask the AI (or open the full designer).

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, CheckCircle2, Sparkles } from 'lucide-react'
import type { ColumnDef, SqlModel, TransactionModel, Validation } from '../txn/types'
import type { CustomizeSession } from './session'
import type { TxnBuildRuntime } from './build/types'
import { formBase } from './txnOps'
import {
  CenterDialog, ChipGroup, PanelNote, PanelRow, Switch, RuleLine, VIOLET, VIOLET_DARK, VIOLET_LINE, VioletButton,
} from './ui'
import more from './build/moreActions.json'
import {
  COMPARE_OPS, isRule, recordCheckText, recordCheckToRule, ruleToRecordCheck, uniqueRuleName,
  type CompareOp, type RecordCheckSentence,
} from './build/checks'
import {
  readEditLock, readFilters, readRowSecurity, writeEditLock, writeFilters, writeRowSecurity,
  type OwnRecords, type RequiredFilter,
} from './build/rowSecurity'
import {
  AFTER_CHANNELS, AFTER_EVENTS, afterFollowup, afterTemplate, afterText, namesToTokens, templateId,
  type AfterChannel, type AfterEvent,
} from './build/followups'
import { kindOf } from './build/fieldKinds'
import { validationSummary, normalizeFollowupAction } from '../txn/types'
import { tw } from '../testHooks'

interface MoreItem { id: string; kind: string; label: string; line: string; section?: string; asks?: string[] }
const MORE = more as { groups: { title: string; items: MoreItem[] }[]; ai_ideas: { screen: string[]; field: string[] } }

const unwrapForm = (s: unknown): SqlModel => ((s as { sql_model?: SqlModel })?.sql_model ?? s) as SqlModel

export interface BuildBarToolsProps {
  session: CustomizeSession
  build: TxnBuildRuntime
  objName: string
  /** The screen's name. */
  title: string
  model: TransactionModel
  headerFormNo: string
  /** What "Ask AI to change" is pointed at ("Expense claim › Amount"). */
  context: string
  /** Opens Additional attributes (Let organizations add their own fields). */
  onOpenAttributes?: () => void
}

export default function BuildBarTools({
  session, build, objName, title, model, headerFormNo, context, onOpenAttributes,
}: BuildBarToolsProps) {
  const forms = (model.sql_models || []).map(unwrapForm)
  const header = forms.find((f) => String(f.form_no ?? '1') === headerFormNo) || forms[0]
  const [checksOpen, setChecksOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialog, setDialog] = useState<MoreItem | null>(null)
  const [aiOpen, setAiOpen] = useState(false)
  const recordChecks = (header?.validations || []).filter((v) => !(isRule(v) && v.disabled))
  const menuBtn = useRef<HTMLButtonElement | null>(null)

  const pick = (it: MoreItem) => {
    setMenuOpen(false)
    if (it.kind === 'version') build.saveVersion?.({ objName, title, changes: session.changeLog })
    else if (it.kind === 'designer') build.openDesigner?.(objName)
    else if (it.kind === 'attributes' && onOpenAttributes) onOpenAttributes()
    else setDialog(it)
  }
  const badge = (it: MoreItem): string => {
    const hf = header as SqlModel & { list_view?: { edit_lock?: unknown; enforced_filter_sets?: unknown[] } }
    switch (it.id) {
      case 'after': { const n = (model as { follow_up_actions?: unknown[] }).follow_up_actions?.length || 0; return n ? `${n} set` : '' }
      case 'rowsec': return readRowSecurity(hf?.row_security).on ? 'On' : ''
      case 'lock': return readEditLock(hf?.list_view?.edit_lock as never) ? 'On' : ''
      case 'filters': return (hf?.list_view?.enforced_filter_sets || []).length ? 'On' : ''
      case 'save': return session.changeLog.length ? `${session.changeLog.length} unsaved` : ''
      default: return ''
    }
  }
  const items = MORE.groups.map((g) => ({
    ...g,
    items: g.items.filter((it) => (it.kind !== 'version' || !!build.saveVersion)
      && (it.kind !== 'designer' || !!build.openDesigner)
      && (it.kind !== 'attributes' || !!onOpenAttributes)),
  })).filter((g) => g.items.length)

  return (
    <>
      <button type="button" style={toolBtn} onClick={() => setChecksOpen(true)} {...tw('action', 'make-changes-record-checks')}>
        <CheckCircle2 size={14} /> Checks before saving · {recordChecks.length}
      </button>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <button ref={menuBtn} type="button" style={toolBtn} aria-haspopup="menu" aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)} {...tw('action', 'make-changes-more')}>
          More actions <ChevronDown size={14} />
        </button>
        {menuOpen && (
          <MoreMenu anchor={menuBtn.current} onClose={() => setMenuOpen(false)}>
            {items.map((g) => (
              <div key={g.title} style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: VIOLET, padding: '8px 10px 2px', textTransform: 'uppercase', letterSpacing: '.06em' }}>{g.title}</span>
                {g.items.map((it) => (
                  <button key={it.id} type="button" role="menuitem" onClick={() => pick(it)} {...tw('action', `make-changes-more:${it.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: 0, borderRadius: 6, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: 'var(--color-text, #111827)' }}>
                    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <strong style={{ fontSize: 14, fontWeight: 600 }}>{it.label}</strong>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #4b5563)' }}>{it.line}</span>
                    </span>
                    {badge(it) && <span style={{ fontSize: 13, fontWeight: 600, color: VIOLET, whiteSpace: 'nowrap' }}>{badge(it)}</span>}
                  </button>
                ))}
              </div>
            ))}
          </MoreMenu>
        )}
      </span>
      {build.askAi && (
        <button type="button" onClick={() => setAiOpen((v) => !v)} aria-expanded={aiOpen} {...tw('action', 'make-changes-ask-ai')}
          style={{ ...toolBtn, background: '#fff', color: VIOLET_DARK, borderColor: '#fff', fontWeight: 700 }}>
          <Sparkles size={14} /> Ask AI to change
        </button>
      )}
      {aiOpen && build.askAi && (
        <AskAiBox context={context} onClose={() => setAiOpen(false)}
          onSend={(text) => build.askAi!(`${context}: ${text}`)} />
      )}
      {checksOpen && header && (
        <RecordChecksDialog session={session} title={title} header={header} forms={forms}
          headerFormNo={headerFormNo} onClose={() => setChecksOpen(false)} />
      )}
      {dialog && header && (() => {
        const close = () => setDialog(null)
        const askAi = build.askAi ? (text: string) => { close(); setAiOpen(true); build.askAi!(text) } : undefined
        switch (dialog.id) {
          case 'after': return <AfterSavingDialog session={session} build={build} objName={objName} title={title} model={model} header={header} onClose={close} askAi={askAi} />
          case 'rowsec': return <RowSecurityDialog session={session} header={header} headerFormNo={headerFormNo} onClose={close} />
          case 'lock': return <LockDialog session={session} header={header} headerFormNo={headerFormNo} onClose={close} />
          case 'filters': return <FiltersDialog session={session} header={header} headerFormNo={headerFormNo} onClose={close} />
          default: return (
            <AskDialog item={dialog} onClose={close}
              onAskAi={askAi ? () => askAi(`${title}: ${dialog.label}`) : undefined}
              onDesigner={build.openDesigner ? () => { close(); build.openDesigner!(objName, dialog.section) } : undefined} />
          )
        }
      })()}
    </>
  )
}

// ── the More actions popover ─────────────────────────────────────────────

function MoreMenu({ anchor, onClose, children }: { anchor: HTMLElement | null; onClose: () => void; children: React.ReactNode }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const W = 380
  useLayoutEffect(() => {
    const r = anchor?.getBoundingClientRect()
    if (r) setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8)) })
  }, [anchor])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  if (typeof document === 'undefined') return null
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9472 }} onMouseDown={onClose}>
      <div role="menu" onMouseDown={(e) => e.stopPropagation()} {...tw('screen', 'make-changes-more-menu')}
        style={{
          position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: W,
          maxHeight: 'calc(100vh - 80px)', overflowY: 'auto', padding: 6, borderRadius: 10,
          background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #dde3ea)',
          boxShadow: '0 12px 32px rgba(0,0,0,.2)', display: 'flex', flexDirection: 'column',
        }}>{children}</div>
    </div>,
    document.body,
  )
}

// ── ✦ Ask AI to change ───────────────────────────────────────────────────

/** The ✦ Ask AI to change box under the mode's bar. It pre-fills the host's
 *  Ask-AI panel (never sends). `ideas` / `placeholder` let another surface (a
 *  website) offer its own starters; a screen's come from moreActions.json. */
export function AskAiBox({ context, onSend, onClose, ideas: ownIdeas, placeholder }: {
  context: string
  onSend: (text: string) => void
  onClose: () => void
  ideas?: string[]
  placeholder?: string
}) {
  const [text, setText] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const field = context.includes(' › ') ? context.split(' › ').pop() || '' : ''
  const ideas = ownIdeas ?? (field ? MORE.ai_ideas.field : MORE.ai_ideas.screen).map((i) => i.replace('{field}', field))
  return (
    <div style={{ flexBasis: '100%', background: '#fff', color: 'var(--color-text, #111827)', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}
      {...tw('screen', 'make-changes-ask-ai')}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <strong style={{ flex: '1 1 auto' }}>Ask AI to change {context}</strong>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #4b5563)' }}>For anything the buttons don’t cover. The AI shows what will change before it changes anything.</span>
      </div>
      {sent === null ? (
        <>
          <textarea aria-label="Describe the change" rows={3} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={placeholder ?? 'e.g. When the category is Travel, ask for the trip number and make the receipt required'}
            {...tw('field', 'make-changes-ai-text')}
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--color-border, #c7cfd9)', borderRadius: 6, padding: '8px 10px', resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #4b5563)' }}>Try:</span>
            {ideas.map((i) => (
              <button key={i} type="button" onClick={() => setText(i)}
                style={{ minHeight: 30, padding: '0 12px', borderRadius: 15, border: `1px solid ${VIOLET_LINE}`, background: '#fff', color: VIOLET_DARK, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>{i}</button>
            ))}
            <span style={{ flex: 1 }} />
            <VioletButton quiet onClick={onClose}>Cancel</VioletButton>
            <VioletButton disabled={!text.trim()} hook="make-changes-ai-send" onClick={() => { onSend(text.trim()); setSent(text.trim()) }}>Send to AI</VioletButton>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#f5f3ff', borderRadius: 8, padding: '10px 12px' }}>
          <span style={{ flex: '1 1 300px' }}>Put in the Ask-AI panel: “{sent}”. Press send there — it shows what it plans to change before applying it.</span>
          <VioletButton quiet onClick={() => { setSent(null); setText('') }}>Ask something else</VioletButton>
        </div>
      )}
    </div>
  )
}

// ── ✓ Checks before saving (the whole record) ────────────────────────────

function RecordChecksDialog({ session, title, header, forms, headerFormNo, onClose }: {
  session: CustomizeSession; title: string; header: SqlModel; forms: SqlModel[]; headerFormNo: string; onClose: () => void
}) {
  const cols = (header.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[]
  const label = (n: string) => {
    const c = cols.find((x) => String(x.db_name || x.name).toLowerCase() === n.toLowerCase())
    return String(c?.heading || c?.name || n)
  }
  const children = forms.filter((f) => String(f.form_no ?? '1') !== headerFormNo)
  const formLabel = (no: string) => String(children.find((f) => String(f.form_no) === no)?.form_title || `form ${no}`)
  const comparable = cols.filter((c) => ['number', 'money', 'date', 'datetime'].includes(kindOf(c).id))
  const validations: Validation[] = header.validations || []
  type T = RecordCheckSentence['type']
  const types: { value: T; label: string }[] = [
    ...(children.length ? [{ value: 'min_lines' as T, label: 'At least some lines' }] : []),
    ...(comparable.length > 1 ? [{ value: 'compare' as T, label: 'One field compared with another' }] : []),
  ]
  const [type, setType] = useState<T | ''>(types[0]?.value ?? '')
  const [form, setForm] = useState(String(children[0]?.form_no ?? ''))
  const [n, setN] = useState('1')
  const [a, setA] = useState('')
  const [op, setOp] = useState<CompareOp>('>')
  const [b, setB] = useState('')
  const [message, setMessage] = useState('')
  const [severity, setSeverity] = useState<'E' | 'W'>('E')
  const draft: RecordCheckSentence | null = type === 'min_lines' ? (form && Number(n) > 0 ? { type, form, n: Math.round(Number(n)) } : null)
    : type === 'compare' ? (a && b && a !== b ? { type, a, op, b } : null) : null
  const fallback = draft ? recordCheckText(draft, label, formLabel) : ''
  const arr = `${formBase(headerFormNo)}.validations`

  const add = async () => {
    if (!draft) return
    const name = uniqueRuleName(draft.type === 'min_lines' ? `lines_${draft.form}` : `${draft.a}_vs_${draft.b}`, validations)
    const rule = recordCheckToRule({ sentence: draft, message: message.trim() || fallback, severity, name })
    if (await session.apply({ set: [], reset: [], ops: [{ op: 'insert', array: arr, item: rule as unknown as Record<string, unknown> }] }, `Check added: ${fallback}`)) {
      setA(''); setB(''); setMessage('')
    }
  }
  const remove = (name: string, text: string) => {
    void session.apply({ set: [], reset: [], ops: [{ op: 'remove', array: arr, key: name }] }, `Check removed: ${text}`)
  }

  return (
    <CenterDialog title={`Checks before saving a ${title} record`} onClose={onClose} hook="make-changes-record-checks">
      <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>
        Checks that look at the whole record. A record that fails one is not saved, and people see the message.
        Checks on one field are in that field’s ⚙ › Checks.
      </span>
      {validations.length === 0 && <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>No checks yet.</span>}
      {validations.map((v, i) => {
        const rc = ruleToRecordCheck(v)
        if (rc) {
          const text = recordCheckText(rc.sentence, label, formLabel)
          return <RuleLine key={rc.name} icon="✓" text={text + (rc.severity === 'W' ? ' — a warning only' : '')}
            sub={`Shows: “${rc.message}”`} onRemove={() => remove(rc.name, text)} />
        }
        const text = typeof v === 'string' ? v : validationSummary(v)
        return <RuleLine key={`u${i}`} icon="✓" text={text} sub="Set up in the full designer" />
      })}
      {types.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 8, border: `1px dashed ${VIOLET_LINE}` }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Add a check</span>
          <ChipGroup label="Kind of check" hook="make-changes-rc-type" value={type} options={types} onChange={(v) => setType(v)} />
          {type === 'min_lines' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              {children.length > 1 && (
                <ChipGroup label="Which lines" hook="make-changes-rc-form" value={form}
                  options={children.map((f) => ({ value: String(f.form_no), label: String(f.form_title || f.form_no) }))} onChange={setForm} />
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>At least how many lines?</span>
                <input type="number" min={1} value={n} onChange={(e) => setN(e.target.value)} style={{ width: 100, height: 32, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 8px' }} />
              </label>
            </div>
          )}
          {type === 'compare' && (
            <>
              <ChipGroup label="This field" hook="make-changes-rc-a" value={a} options={comparable.map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name) }))} onChange={setA} />
              <ChipGroup label="must be" hook="make-changes-rc-op" value={op} options={COMPARE_OPS.map((o) => ({ value: o.op, label: o.label }))} onChange={setOp} />
              <ChipGroup label="That field" hook="make-changes-rc-b" value={b} options={comparable.filter((c) => String(c.db_name || c.name) !== a).map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name) }))} onChange={setB} />
            </>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Message people see</span>
            <input value={message} placeholder={fallback || 'Written for you once the check is complete'} onChange={(e) => setMessage(e.target.value)}
              style={{ height: 34, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
          </label>
          <ChipGroup label="If it fails" hook="make-changes-rc-severity" value={severity}
            options={[{ value: 'E', label: 'Stop saving' }, { value: 'W', label: 'Warn, then let them save' }]} onChange={setSeverity} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 13.5, color: draft ? VIOLET : 'var(--color-text-secondary, #4b5563)' }}>{draft ? `${fallback}.` : 'Fill in the blanks above.'}</span>
            <VioletButton disabled={!draft} hook="make-changes-add-record-check" onClick={() => { void add() }}>Add check</VioletButton>
          </div>
        </div>
      ) : (
        <PanelNote>This screen has no lines and fewer than two numbers or dates to compare. Ask the AI for anything else.</PanelNote>
      )}
      {session.errors.length > 0 && <PanelNote tone="warn">Not saved. {session.errors.join(' · ')}</PanelNote>}
    </CenterDialog>
  )
}

// ── After saving… ────────────────────────────────────────────────────────

function AfterSavingDialog({ session, build, objName, title, model, header, onClose, askAi }: {
  session: CustomizeSession; build: TxnBuildRuntime; objName: string; title: string; model: TransactionModel
  header: SqlModel; onClose: () => void; askAi?: (text: string) => void
}) {
  const cols = (header.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[]
  const label = (n: string) => {
    const c = cols.find((x) => String(x.db_name || x.name).toLowerCase() === n.toLowerCase())
    return String(c?.heading || c?.name || n)
  }
  const list = ((model as { follow_up_actions?: unknown[] }).follow_up_actions || []).map((a) => normalizeFollowupAction(a as never))
  const [toByTemplate, setToByTemplate] = useState<Record<string, string>>({})
  useEffect(() => {
    let cancelled = false
    build.transport.listTemplates?.(objName)
      .then((ts) => {
        if (cancelled) return
        const m: Record<string, string> = {}
        for (const t of ts) {
          const from = t.recipients?.[0]?.from as { column?: string } | undefined
          if (from?.column) m[t.id] = from.column
        }
        setToByTemplate(m)
      })
      .catch(() => { /* names only */ })
    return () => { cancelled = true }
  }, [build, objName])
  const [event, setEvent] = useState<AfterEvent>('on-add')
  const [does, setDoes] = useState<AfterChannel | 'other'>('email')
  const [to, setTo] = useState('')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Who can receive it: fields that hold an address (email / phone) or pick
  // a person from another screen.
  const toFields = cols.filter((c) => {
    const k = kindOf(c).id
    return does === 'email' ? (k === 'email' || /mail/i.test(String(c.db_name))) : does === 'other' ? false
      : (k === 'phone' || /phone|mobile|whatsapp/i.test(String(c.db_name)))
  })
  const canSend = !!build.transport.saveTemplate
  const ready = does === 'other' ? !!text.trim() : (!!to && !!text.trim() && canSend)
  const when = AFTER_EVENTS.find((e) => e.id === event)?.label || ''
  const sentence = does === 'other'
    ? `When a ${title} record ${when}, ${text.trim() || '…'}`
    : `When a ${title} record ${when}, send a${does === 'email' ? 'n' : ''} ${AFTER_CHANNELS.find((c) => c.id === does)?.label.toLowerCase()} to the person in ${to ? label(to) : '…'}, saying “${text.trim() || '…'}”`

  const add = async () => {
    if (!ready) return
    if (does === 'other') { askAi?.(`${title}: ${sentence}`); return }
    setBusy(true)
    setError(null)
    try {
      const id = templateId(objName, list.map((l) => String(l.send?.template || '')))
      const tpl = afterTemplate({ id, objName, title: `${title}: ${when}`, channel: does, toColumn: to, text: namesToTokens(text.trim(), cols) })
      const saved = await build.transport.saveTemplate!(objName, tpl)
      const name = uniqueName(`notify_${does}_${event.replace(/^on-/, '').replace(/-/g, '_')}`, list.map((l) => l.name))
      const fa = afterFollowup({ name, event, channel: does, templateId: saved?.id || id })
      if (await session.apply({ set: [], reset: [], ops: [{ op: 'insert', array: 'transaction.follow_up_actions', item: fa as unknown as Record<string, unknown> }] }, `After saving: ${sentence}`)) {
        setText('')
      }
    } catch (e) {
      setError(String((e as Error)?.message || e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <CenterDialog title="After saving…" onClose={onClose} hook="make-changes-after-saving">
      <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>Things that happen by themselves once a record is saved.</span>
      {list.length === 0 && <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>Nothing happens after saving yet.</span>}
      {list.map((fa) => {
        const t = afterText(fa, label, toByTemplate[String(fa.send?.template || '')])
        return <RuleLine key={fa.name} icon="↪" text={t}
          onRemove={() => { void session.apply({ set: [], reset: [], ops: [{ op: 'remove', array: 'transaction.follow_up_actions', key: fa.name }] }, `Removed: ${t}`) }} />
      })}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 8, border: `1px dashed ${VIOLET_LINE}` }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Add what should happen</span>
        <span style={{ fontSize: 13.5 }}>When a {title} record…</span>
        <ChipGroup label="When" hook="make-changes-after-when" value={event} options={AFTER_EVENTS.map((e) => ({ value: e.id, label: e.label }))} onChange={setEvent} />
        <span style={{ fontSize: 13.5 }}>then</span>
        <ChipGroup label="Then" hook="make-changes-after-does" value={does}
          options={[...AFTER_CHANNELS.map((c) => ({ value: c.id as AfterChannel | 'other', label: `Send ${c.id === 'email' ? 'an email' : `a ${c.label}`}` })),
            ...(askAi ? [{ value: 'other' as const, label: 'Something else (the AI builds it)' }] : [])]}
          onChange={(v) => { setDoes(v); setTo('') }} />
        {does !== 'other' && (
          <>
            <span style={{ fontSize: 13.5 }}>to the person in</span>
            {toFields.length
              ? <ChipGroup label="To" hook="make-changes-after-to" value={to} options={toFields.map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name) }))} onChange={setTo} />
              : <PanelNote>No field on this screen holds {does === 'email' ? 'an email address' : 'a phone number'} yet — add one, or ask the AI.</PanelNote>}
          </>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{does === 'other' ? 'Describe it' : 'Saying'}</span>
          <input value={text} onChange={(e) => setText(e.target.value)} {...tw('field', 'make-changes-after-text')}
            placeholder={does === 'other' ? 'e.g. add the amount to the employee’s monthly total' : 'e.g. New claim {Claim no} is waiting for you'}
            style={{ height: 34, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
        </label>
        {does !== 'other' && !canSend && <PanelNote>Messages can’t be set up here on this server — ask the AI instead.</PanelNote>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 13.5, color: ready ? VIOLET : 'var(--color-text-secondary, #4b5563)' }}>{ready ? `${sentence}.` : 'Fill in the blanks above.'}</span>
          <VioletButton disabled={!ready || busy} hook="make-changes-add-after" onClick={() => { void add() }}>
            {does === 'other' ? 'Ask the AI' : 'Add'}
          </VioletButton>
        </div>
      </div>
      {(error || session.errors.length > 0) && <PanelNote tone="warn">Not saved. {error || session.errors.join(' · ')}</PanelNote>}
    </CenterDialog>
  )
}

function uniqueName(base: string, taken: string[]): string {
  let name = base
  for (let n = 2; taken.includes(name); n++) name = `${base}_${n}`
  return name
}

// ── Who sees which records ───────────────────────────────────────────────

function RowSecurityDialog({ session, header, headerFormNo, onClose }: {
  session: CustomizeSession; header: SqlModel; headerFormNo: string; onClose: () => void
}) {
  const cols = (header.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[]
  const main = String(header.main_table || '').toLowerCase()
  const candidates = cols.filter((c) => String(c.table_name || main).toLowerCase() === main
    && !String(c.sql_expression ?? '').trim() && ['words', 'pick', 'email'].includes(kindOf(c).id))
  // Fields filled with the person's sign-in come first.
  const stamped = (c: ColumnDef) => /logged_in_user\(\)|:audit_(add|chg)_user/i.test(String(c.default_value || ''))
  candidates.sort((x, y) => Number(stamped(y)) - Number(stamped(x)))
  const cur = readRowSecurity(header.row_security)
  const [roleDraft, setRoleDraft] = useState('')
  const path = `${formBase(headerFormNo)}.row_security`
  const write = (next: OwnRecords, what: string) => {
    const rs = writeRowSecurity(next)
    void session.apply(rs ? { set: [{ path, value: rs }], reset: [] } : { set: [], reset: [path] }, what)
  }
  const label = (n: string) => String(cols.find((c) => String(c.db_name || c.name) === n)?.heading || n)
  const sentence = !cur.on ? 'Everyone who can open this screen sees every record.'
    : `Each person sees only the records whose ${label(cur.column)} is them${cur.exempt.length ? `; ${cur.exempt.join(', ')} still see every record` : ''}.`
  return (
    <CenterDialog title="Who sees which records" onClose={onClose} hook="make-changes-row-security">
      <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>
        Decide whose records each person can see. It applies everywhere this data is read — screens, lists and reports.
      </span>
      {cur.unreadable ? (
        <PanelNote>Records are already shared out in a way set up in the full designer.</PanelNote>
      ) : (
        <>
          <PanelRow title="Each person sees only their own records">
            <Switch label="Each person sees only their own records" on={cur.on}
              onChange={(on) => write({ ...cur, on, column: cur.column || String(candidates[0]?.db_name || '') },
                on ? 'Each person sees only their own records' : 'Everyone sees every record')} />
          </PanelRow>
          {cur.on && (
            <>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Whose record is it? The person in</span>
              <ChipGroup label="Whose record" hook="make-changes-rowsec-field" value={cur.column}
                options={candidates.map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name) }))}
                onChange={(column) => write({ ...cur, column }, `Records belong to the ${label(column)}`)} />
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #4b5563)' }}>
                The field must hold the person’s sign-in — a field that starts with “The person filling it in” does.
              </span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>These roles still see every record</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cur.exempt.map((r) => (
                  <span key={r} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 30, padding: '0 4px 0 10px', borderRadius: 15, background: '#ede9fe', color: VIOLET_DARK, fontWeight: 500 }}>
                    {r}
                    <button type="button" aria-label={`Remove ${r}`} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: VIOLET_DARK }}
                      onClick={() => write({ ...cur, exempt: cur.exempt.filter((x) => x !== r) }, `${r} now sees only their own`)}>×</button>
                  </span>
                ))}
                <input value={roleDraft} onChange={(e) => setRoleDraft(e.target.value)} placeholder="Add a role, e.g. finance"
                  onKeyDown={(e) => {
                    const r = roleDraft.trim()
                    if (e.key === 'Enter' && r && !cur.exempt.includes(r)) {
                      write({ ...cur, exempt: [...cur.exempt, r] }, `${r} sees every record`)
                      setRoleDraft('')
                    }
                  }}
                  style={{ height: 30, borderRadius: 15, border: `1px solid ${VIOLET_LINE}`, padding: '0 12px', fontFamily: 'inherit' }} />
              </div>
            </>
          )}
        </>
      )}
      <div style={{ fontSize: 14, color: VIOLET }}>{sentence}</div>
      {session.errors.length > 0 && <PanelNote tone="warn">Not saved. {session.errors.join(' · ')}</PanelNote>}
    </CenterDialog>
  )
}

// ── Lock records when… ───────────────────────────────────────────────────

function LockDialog({ session, header, headerFormNo, onClose }: {
  session: CustomizeSession; header: SqlModel; headerFormNo: string; onClose: () => void
}) {
  const cols = (header.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[]
  const lv = (header as SqlModel & { list_view?: { edit_lock?: unknown } }).list_view
  const cur = readEditLock(lv?.edit_lock as never)
  const [field, setField] = useState(cur?.field || '')
  const [value, setValue] = useState(cur?.value || '')
  const [reason, setReason] = useState(cur?.reason || '')
  const path = `${formBase(headerFormNo)}.list_view.edit_lock`
  const options = cols.filter((c) => !c.uda && ['choice', 'words', 'yesno'].includes(kindOf(c).id))
    .map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name) }))
  const label = (n: string) => options.find((o) => o.value === n)?.label || n
  const ready = !!field && !!value.trim()
  const save = () => {
    const rule = writeEditLock({ field, value, reason })
    if (rule) void session.apply({ set: [{ path, value: rule }], reset: [] }, `Locked when ${label(field)} is ${value.trim()}`)
  }
  return (
    <CenterDialog title="Lock records when…" onClose={onClose} hook="make-changes-lock"
      footer={(
        <>
          {cur && <VioletButton quiet onClick={() => { void session.apply({ set: [], reset: [path] }, 'Records never lock') }}>Stop locking</VioletButton>}
          <VioletButton disabled={!ready} hook="make-changes-lock-save" onClick={save}>{cur ? 'Change' : 'Lock them'}</VioletButton>
        </>
      )}>
      <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>A locked record opens read-only for everyone, and nobody can save changes to it.</span>
      {cur?.unreadable && <PanelNote>Records already lock by a rule set up in the full designer. Saving here replaces it.</PanelNote>}
      <span style={{ fontSize: 13, fontWeight: 600 }}>Lock a record when</span>
      <ChipGroup label="Lock when" hook="make-changes-lock-field" value={field} options={options} onChange={setField} />
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>is</span>
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. Approved"
          style={{ height: 34, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Message people see</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Approved claims can’t be changed"
          style={{ height: 34, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
      </label>
      {ready && <div style={{ fontSize: 14, color: VIOLET }}>A record locks once {label(field)} is “{value.trim()}”.</div>}
      {session.errors.length > 0 && <PanelNote tone="warn">Not saved. {session.errors.join(' · ')}</PanelNote>}
    </CenterDialog>
  )
}

// ── Filters people must use ──────────────────────────────────────────────

function FiltersDialog({ session, header, headerFormNo, onClose }: {
  session: CustomizeSession; header: SqlModel; headerFormNo: string; onClose: () => void
}) {
  const cols = (header.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[]
  const main = String(header.main_table || '').toLowerCase()
  const lv = (header as SqlModel & { list_view?: { enforced_filter_sets?: never } }).list_view
  const cur = useMemo(() => readFilters(lv?.enforced_filter_sets), [lv])
  const [filters, setFilters] = useState<RequiredFilter[]>(cur.filters)
  const path = `${formBase(headerFormNo)}.list_view.enforced_filter_sets`
  const options = cols.filter((c) => String(c.table_name || main).toLowerCase() === main && !c.uda && !String(c.sql_expression ?? '').trim())
    .map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name), date: ['date', 'datetime'].includes(kindOf(c).id) }))
  const label = (n: string) => options.find((o) => o.value === n)?.label || n
  const save = () => {
    const sets = writeFilters(filters)
    void session.apply(sets ? { set: [{ path, value: sets }], reset: [] } : { set: [], reset: [path] },
      sets ? `Filters people must use: ${filters.map((f) => label(f.column)).join(', ')}` : 'No filters people must use')
  }
  const toggle = (col: string) => {
    const has = filters.some((f) => f.column === col)
    setFilters(has ? filters.filter((f) => f.column !== col)
      : [...filters, { column: col, value: '', locked: false, range: !!options.find((o) => o.value === col)?.date }])
  }
  return (
    <CenterDialog title="Filters people must use" onClose={onClose} hook="make-changes-filters"
      footer={<VioletButton hook="make-changes-filters-save" onClick={save}>Save</VioletButton>}>
      <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>The list asks for these before it shows records — useful when there are many.</span>
      {cur.unreadable && <PanelNote>Filters are already set up in the full designer. Saving here replaces them.</PanelNote>}
      <span style={{ fontSize: 13, fontWeight: 600 }}>People must filter by</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const on = filters.some((f) => f.column === o.value)
          return (
            <button key={o.value} type="button" aria-pressed={on} onClick={() => toggle(o.value)}
              style={{ minHeight: 30, padding: '0 12px', borderRadius: 15, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5,
                border: `1px solid ${on ? VIOLET : VIOLET_LINE}`, background: on ? VIOLET : '#fff', color: on ? '#fff' : VIOLET_DARK }}>
              {on ? '✓ ' : '+ '}{o.label}
            </button>
          )
        })}
      </div>
      {filters.map((f, i) => (
        <div key={f.column} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', padding: '8px 10px', borderRadius: 8, background: '#f5f3ff' }}>
          <strong style={{ minWidth: 140 }}>{label(f.column)}{f.range ? ' (from – to)' : ''}</strong>
          <input value={f.value} placeholder={f.range ? 'Starts from, e.g. :calendar_month_start' : 'Starting value (optional)'}
            onChange={(e) => setFilters(filters.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
            style={{ flex: '1 1 200px', height: 32, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}>
            <input type="checkbox" checked={!f.locked} onChange={(e) => setFilters(filters.map((x, j) => (j === i ? { ...x, locked: !e.target.checked } : x)))} />
            People may change it
          </label>
        </div>
      ))}
      {session.errors.length > 0 && <PanelNote tone="warn">Not saved. {session.errors.join(' · ')}</PanelNote>}
    </CenterDialog>
  )
}

// ── the rest: what the AI will ask ───────────────────────────────────────

function AskDialog({ item, onClose, onAskAi, onDesigner }: {
  item: MoreItem; onClose: () => void; onAskAi?: () => void; onDesigner?: () => void
}) {
  return (
    <CenterDialog title={item.label} onClose={onClose} width={600} hook={`make-changes-ask:${item.id}`}
      footer={(
        <>
          {onDesigner && <VioletButton quiet onClick={onDesigner}>Open in the full designer</VioletButton>}
          {onAskAi && <VioletButton onClick={onAskAi} hook={`make-changes-ask-ai:${item.id}`}>✦ Ask AI to set this up</VioletButton>}
        </>
      )}>
      <span style={{ color: 'var(--color-text-secondary, #4b5563)' }}>{item.line}. A few questions, each with a suggested answer, set it up.</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>It will ask you</span>
      {(item.asks || []).map((q) => <RuleLine key={q} icon="?" text={q} />)}
    </CenterDialog>
  )
}

const toolBtn: React.CSSProperties = {
  height: 30, padding: '0 12px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid rgba(255,255,255,.45)', background: 'rgba(255,255,255,.08)', color: '#fff',
  font: '600 13px inherit', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
}

